const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 300;

/**
 * @param {(...args: unknown[]) => void} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;
  const debounced = /** @type {typeof fn & { cancel: () => void }} */ (
    (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    }
  );
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

class HeaderDropdownMenu extends HTMLElement {
  connectedCallback() {
    this.#bindHoverMenus('[data-mega-menu]', '.js-mega-menu-trigger', '.header-mega-menu');
    this.#bindHoverMenus('[data-dropdown-menu]', '.js-menu-toggle', '.header-dropdown-menu__item-submenu-wrapper');
  }

  /**
   * @param {string} itemSelector
   * @param {string} triggerSelector
   * @param {string} panelSelector
   */
  #bindHoverMenus(itemSelector, triggerSelector, panelSelector) {
    const items = this.querySelectorAll(itemSelector);

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const trigger = item.querySelector(triggerSelector);
      const panel = item.querySelector(panelSelector);

      if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      const open = () => {
        this.#closeAllMenus(item);
        trigger.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        item.classList.add('is-open');
      };

      const close = () => {
        trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        item.classList.remove('is-open');
      };

      const debouncedClose = debounce(close, DEACTIVATE_DELAY);
      const debouncedOpen = debounce(open, ACTIVATE_DELAY);

      const handleEnter = () => {
        debouncedClose.cancel();
        debouncedOpen();
      };

      const handleLeave = () => {
        debouncedOpen.cancel();
        debouncedClose();
      };

      item.addEventListener('pointerenter', handleEnter);
      item.addEventListener('pointerleave', handleLeave);
      item.addEventListener('focusin', handleEnter);
      item.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
        handleLeave();
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
      });
    });
  }

  /**
   * @param {HTMLElement} [except]
   */
  #closeAllMenus(except) {
    this.querySelectorAll('[data-mega-menu].is-open, [data-dropdown-menu].is-open').forEach((item) => {
      if (item === except) return;
      if (!(item instanceof HTMLElement)) return;

      const trigger =
        item.querySelector('.js-mega-menu-trigger') ?? item.querySelector('.js-menu-toggle');
      const panel =
        item.querySelector('.header-mega-menu') ??
        item.querySelector('.header-dropdown-menu__item-submenu-wrapper');

      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (panel instanceof HTMLElement) {
        panel.setAttribute('aria-hidden', 'true');
      }
      item.classList.remove('is-open');
    });
  }
}

if (!customElements.get('header-dropdown-menu')) {
  customElements.define('header-dropdown-menu', HeaderDropdownMenu);
}
